import {
	AuthenticationResult,
	EventType,
	PublicClientApplication
} from '@azure/msal-browser'
import config from '@utils/config'

// Config object to be passed to Msal on creation
export const msalConfig = {
	auth: {
		clientId: `${process.env.CLIENT_ID}`,
		authority: `${process.env.AUTHORITY}`,
		knownAuthorities: process.env.KNOWN_AUTHORITIES?.split(', '),
		redirectUri: '/auth',
		postLogoutRedirectUri: '/',
		navigateToLoginRequestUrl: false
	},
	cache: {
		cacheLocation: 'localStorage' // The user should stay connect if he/she opens a new tab.
	}
}

// Shared MSAL singleton used across the entire app.
export const msalInstance = new PublicClientApplication(msalConfig)
msalInstance.enableAccountStorageEvents()

const forEachStorageKey = (
	storage: Storage,
	callback: (key: string) => void
) => {
	for (let i = storage.length - 1; i >= 0; i--) {
		const key = storage.key(i)
		if (key) {
			callback(key)
		}
	}
}

const clearAllB2cAccountScopedKeys = () => {
	const isB2cAccountScopedKey = (key: string) => {
		const normalizedKey = key.toLowerCase()
		return (
			normalizedKey.includes('-b2c_') &&
			normalizedKey.includes('.b2clogin.com-')
		)
	}

	const clearB2cKeys = (storage: Storage) => {
		forEachStorageKey(storage, (key) => {
			if (isB2cAccountScopedKey(key)) {
				storage.removeItem(key)
			}
		})
	}

	clearB2cKeys(localStorage)
	clearB2cKeys(sessionStorage)
}

const clearMsalBrowserStorage = () => {
	const isMsalKey = (key: string) => key.startsWith('msal.')

	forEachStorageKey(localStorage, (key) => {
		if (isMsalKey(key)) {
			localStorage.removeItem(key)
		}
	})

	forEachStorageKey(sessionStorage, (key) => {
		if (isMsalKey(key)) {
			sessionStorage.removeItem(key)
		}
	})
}

msalInstance.addEventCallback((event) => {
	if (
		event.eventType === EventType.LOGIN_SUCCESS ||
		event.eventType === EventType.SSO_SILENT_SUCCESS
	) {
		const payload = event.payload as AuthenticationResult | null
		if (payload?.account) {
			msalInstance.setActiveAccount(payload.account)
		}
		return
	}

	if (event.eventType === EventType.LOGOUT_SUCCESS) {
		msalInstance.setActiveAccount(null)
		localStorage.removeItem(config.localStorage.authUserKey)
		clearAllB2cAccountScopedKeys()
		clearMsalBrowserStorage()
	}
})

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest = {
	scopes: ['openid', 'offline_access']
}

export const authorityCreateAccount = {
	authority: `${process.env.AUTHORITY_CREATE_ACCOUNT}`
}

export const graphConfig = {
	graphMeEndpoint: 'https://graph.microsoft-ppe.com/v1.0/me'
}

export const authRedirectPage: string = '/auth-redirect'

export const acquireAccessToken = async () => {
	const activeAccount = msalInstance.getActiveAccount()
	const accounts = msalInstance.getAllAccounts()

	if (!activeAccount) {
		if (accounts.length === 0) {
			throw new Error('Error while acquiring token. User is not signed in.')
		}
		throw new Error(
			'Error while acquiring token: no active account set. Please log in again.'
		)
	}

	const request = {
		scopes: loginRequest.scopes,
		account: activeAccount
	}

	try {
		const authResult = await msalInstance.acquireTokenSilent(request)
		return authResult.idToken
	} catch (error: any) {
		const errorCode = error.errorCode
		if (
			errorCode === 'consent_required' ||
			errorCode === 'interaction_required'
		) {
			await msalInstance.acquireTokenRedirect(request)
		}
		throw error
	}
}
